/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import {
  MAX_IMAGE_UPLOAD_BYTES,
  MIN_IMAGE_UPLOAD_BYTES,
  base64ToFile,
  convertFileToWebP,
} from "@/shared/utils/images-manipulation";

import { PropertiesService } from "@services/requests";

/**
 * Requests a presigned S3 upload target for a property image via
 * `PropertiesService.getLinkImageProperty`.
 *
 * @param agency_id - The owning agency
 * @param property_id - The property the image belongs to
 * @returns The presigned `{ url, fields }` payload used to POST the file to S3
 * @throws {Error} If the backend response lacks a `url` or `fields`
 */
async function obtainPresignedUrl(agency_id: string, property_id: string): Promise<any> {
  const imageUrlData = await PropertiesService.getLinkImageProperty(agency_id, property_id) as any;
  if (!imageUrlData?.url || !imageUrlData?.fields) {
    console.error('[ImageUpload] Resposta inválida:', imageUrlData);
    throw new Error('Resposta inválida ao gerar link de upload (sem url ou fields)');
  }
  return imageUrlData;
}

/**
 * Converts an image file to WebP, falling back to the original file when the
 * conversion fails so uploads are never blocked by a codec error.
 *
 * @param file - The source image file
 * @returns The WebP-encoded file, or the original file on conversion failure
 */
async function convertToWebP(file: File): Promise<File> {
  try {
    return await convertFileToWebP(file);
  } catch (convErr) {
    console.warn('[ImageUpload] Conversão WebP falhou, usando arquivo original:', convErr);
    return file;
  }
}

/**
 * Enforces the upload size bounds on the converted image, matching the
 * `content-length-range` condition the backend put on the presigned POST.
 *
 * @param file - The original file, used for the error message
 * @param webpFile - The converted file whose size is validated
 * @throws {Error} If the converted file falls outside the accepted size bounds
 */
function validateFileSize(file: File, webpFile: File): void {
  if (webpFile.size > MAX_IMAGE_UPLOAD_BYTES) {
    const limitMB = MAX_IMAGE_UPLOAD_BYTES / 1024 / 1024;
    throw new Error(`Imagem "${file.name}" excede ${limitMB}MB após conversão (${(webpFile.size / 1024 / 1024).toFixed(1)}MB)`);
  }
  if (webpFile.size < MIN_IMAGE_UPLOAD_BYTES) {
    throw new Error(`Imagem "${file.name}" é muito pequena (< 1KB)`);
  }
}

/**
 * Assembles the multipart form data for an S3 presigned POST, appending each
 * string field returned by the presign and the file itself last.
 *
 * @param fields - The presigned POST fields from S3 (only string values are used)
 * @param file - The file to upload under the `file` key
 * @returns The populated FormData ready to POST to the presigned URL
 */
function buildUploadFormData(fields: Record<string, unknown>, file: File): FormData {
  const formData = new FormData();
  Object.entries(fields)
    .filter(([_, value]) => typeof value === "string")
    .forEach(([key, value]) => formData.append(key, value as string));
  formData.append("file", file);
  return formData;
}

/**
 * Uploads a single image end to end: obtains a presigned URL, converts to WebP,
 * validates size, and POSTs the file to S3.
 *
 * @param file - The image file to upload
 * @param agency_id - The owning agency
 * @param property_id - The property the image belongs to
 * @param index - Zero-based position of this file within the batch (for logging)
 * @param total - Total number of files in the batch (for logging)
 */
async function uploadSingleFile(file: File, agency_id: string, property_id: string, index: number, total: number) {
  console.log(`[ImageUpload] Uploading file ${index + 1}/${total}: ${file.name} (${(file.size / 1024).toFixed(0)}KB, ${file.type})`);

  const imageUrlData = await obtainPresignedUrl(agency_id, property_id);
  console.log(`[ImageUpload] Presigned URL obtida: ${imageUrlData.url}`);

  const webpFile = await convertToWebP(file);
  console.log(`[ImageUpload] Convertido para WebP: ${(webpFile.size / 1024).toFixed(0)}KB`);
  validateFileSize(file, webpFile);

  const formData = buildUploadFormData(imageUrlData.fields, webpFile);
  await sendImageToS3(imageUrlData.url, formData);
  console.log(`[ImageUpload] ✓ Upload concluído: ${file.name}`);
}

/**
 * Upload File objects to S3 for a property.
 * Used by PropertyFormDualPane which stores images as File objects.
 */
export async function uploadPropertyFileImages(
  files: File[],
  property_id: string,
  agency_id: string
) {
  if (!files.length) return;

  for (let i = 0; i < files.length; i++) {
    await uploadSingleFile(files[i], agency_id, property_id, i, files.length);
  }
}

/**
 * uploadPropertyImagesOnS3 function.
 *
 * @param {*} images - Parameter description
 * @param {*} property_id - Parameter description
 * @param {*} agency_id - Parameter description
 *
 * @returns {*} Return value description
 */
export async function uploadPropertyImagesOnS3(
  images: string[],
  property_id: string,
  agency_id: string = " "
) {
  try {
    const imageUrls = await Promise.all(
      images.map(() => PropertiesService.getLinkImageProperty(agency_id, property_id))
    ) as any[];

    const imageFiles = await Promise.all(
      images.map(async (base64, index) => {
        const originalFile = base64ToFile(base64, `image_${index}`);
        return convertFileToWebP(originalFile);
      })
    );

    const uploadPromises = imageFiles.map(async (imageFile, index) => {
      const imageUrlData = imageUrls[index];
      const formData = new FormData();
      Object.entries(imageUrlData.fields)
        .filter(([_, value]) => typeof value === "string")
        .forEach(([key, value]) => formData.append(key, value as string));

      formData.append("file", imageFile);
      await sendImageToS3(imageUrlData.url, formData);
    });

    await Promise.all(uploadPromises);

  } catch (error) {
    console.error("Erro durante o upload das imagens:", error);
    throw error;
  }
}

/**
 * sendImageToS3 function.
 *
 * @param {*} url - Parameter description
 * @param {*} image - Parameter description
 *
 * @returns {*} Return value description
 */
export async function sendImageToS3(url: string, image: FormData) {
  const response = await fetch(url, {
    method: "POST",
    body: image
  });

  if (!response.ok) {
    throw new Error(`Falha ao enviar o arquivo: ${response.statusText}`);
  }

  console.log('Arquivo enviado com sucesso.');
}
